import { createField, type ScalarField } from './grid.ts';
import { EVOLUTION_MODEL, volumetricLwc } from './evolution.ts';
import { Grain, WEAK_LAYER_GRAINS, type SnowColumns } from './snowpack.ts';

/**
 * Slab stability.
 *
 * Every layer of every column is a candidate failure layer. For each, the slab is everything
 * above it, and the stability is the layer's shear strength over the shear stress on it:
 *
 * - Natural: S_N = Σ / τ, with τ = ρ̄·g·h·sinψ·cosψ from the slab's weight on the slope.
 * - Skier (SK38, Jamieson & Johnston 1998): S = Σ / (τ + Δτ), where Δτ is the extra shear from a
 *   skier as a line load R on the surface (Föhn 1987), at the weak layer's slope-normal depth
 *   reduced by ski penetration. Interpretation (Jamieson): < 1 poor, 1–1.5 fair, > 1.5 good.
 *
 * Shear strength Σ (kPa) = A_grain·(ρ/ρ_ice)² with grain-specific A, times a bonding factor for
 * young snow (new snow and wind slab gain strength over hours–days, faster when warm), a slow
 * gain for persistent weak layers, and a large loss when wet. A layer can also fail at its top
 * interface: where younger snow lies on it, the bond between the two starts weak and grows with
 * the upper layer's age — which is what makes a fresh wind slab on an old surface touchy for a
 * day or two even when neither layer is weak in itself. The coefficients are chosen to land
 * in the ranges measured with shear frames (Jamieson & Johnston 2001: new snow ~0.2–1 kPa,
 * rounded grains ~2–10 kPa, facets/depth hoar ~0.5–1.5 kPa, buried surface hoar ~0.3–1.5 kPa).
 * They give the right ordering and magnitudes, not site-specific values.
 */

export const STABILITY_MODEL = {
  iceDensity: 917,
  /** A in Σ = A·(ρ/ρ_ice)² (kPa) by grain. */
  strengthCoef: {
    [Grain.PP]: 20,
    [Grain.DF]: 35,
    [Grain.RG]: 55,
    [Grain.RGwp]: 50,
    [Grain.FC]: 13,
    [Grain.DH]: 10,
    [Grain.MF]: 60,
    [Grain.IF]: 400,
    // Avalanche debris: dense and quickly sintered; treated like dense rounded grains.
    [Grain.Debris]: 55,
  } as Partial<Record<Grain, number>>,
  /** Fixed strengths (kPa) for layers whose strength hardly depends on density. */
  surfaceHoarStrength: 0.5,
  graupelStrength: 0.4,
  /** Young-snow bonding: strength × (1 − (1 − min)·e^(−age/τ)), τ = τ0·e^(−k·T). */
  bonding: { min: 0.2, tau0Hours: 24, tempCoef: 0.05 },
  /** Persistent weak layers gain strength slowly: × (1 + rate·ageDays), capped. */
  persistentGainPerDay: 0.02,
  persistentGainCap: 1.6,
  /** Wet snow: strength × max(min, 1 − LWC/lwcAtMin). */
  wet: { min: 0.15, lwcAtMin: 0.04 },
  /** Skier line load (N/m), Föhn (1987). */
  skierLoad: 780,
  /** Ski penetration (m) = coef / mean density of the top 30 cm (Jamieson). */
  penetrationCoef: 34.6,
  maxPenetration: 0.3,
  /**
   * Slabs thinner than this (m) aren't slabs: vertical thickness for the natural index, and
   * slope-normal thickness *below the ski* (after penetration) for the skier index — SK38 is
   * meaningless when the ski is practically standing on the weak layer.
   */
  minSlab: 0.1,
  /** SK38 classes. */
  poor: 1,
  fair: 1.5,
  /** Values are capped here so fields stay finite. */
  maxIndex: 9.99,
  /** Only slopes at least this steep get an avalanche problem. */
  problemMinSlope: 30,
  /**
   * Wet loose: a surface layer at least `wetLooseMinDepth` thick with volumetric LWC at or above
   * this (Fierz "wet", 3 %) is a wet-snow problem on steep slopes even without a slab.
   */
  wetLooseLwc: 0.03,
  wetLooseMinDepth: 0.05,
} as const;

const G = 9.81;
const DEG = Math.PI / 180;

export const AvalancheProblem = {
  None: 0,
  NewSnow: 1,
  WindSlab: 2,
  PersistentSlab: 3,
  WetSnow: 4,
} as const;
export type AvalancheProblem = (typeof AvalancheProblem)[keyof typeof AvalancheProblem];

export const AVALANCHE_PROBLEM_LABELS: Record<AvalancheProblem, string> = {
  [AvalancheProblem.None]: 'None',
  [AvalancheProblem.NewSnow]: 'New snow',
  [AvalancheProblem.WindSlab]: 'Wind slab',
  [AvalancheProblem.PersistentSlab]: 'Persistent weak layer',
  [AvalancheProblem.WetSnow]: 'Wet snow',
};

/** Strength (kPa) from grain and density alone — before bonding, aging and wetness. */
export function baseStrength(grain: Grain, density: number): number {
  const m = STABILITY_MODEL;
  if (grain === Grain.SH) return m.surfaceHoarStrength;
  if (grain === Grain.PPgp) return m.graupelStrength;
  const r = density / m.iceDensity;
  return (m.strengthCoef[grain] ?? m.strengthCoef[Grain.RG]!) * r * r;
}

/** Strength multiplier for liquid water content (volume fraction). */
export function wetFactor(lwc: number): number {
  const w = STABILITY_MODEL.wet;
  return lwc > 0 ? Math.max(w.min, 1 - lwc / w.lwcAtMin) : 1;
}

/** Shear strength (kPa) of a layer. */
export function shearStrength(
  grain: Grain,
  density: number,
  ageHours: number,
  snowTemp: number,
  lwc: number,
): number {
  const m = STABILITY_MODEL;
  let sigma = baseStrength(grain, density);
  if (grain === Grain.PP || grain === Grain.DF || grain === Grain.RGwp || grain === Grain.PPgp) {
    sigma *= bondingFactor(ageHours, snowTemp);
  }
  if (WEAK_LAYER_GRAINS.has(grain) && grain !== Grain.PPgp) {
    sigma *= Math.min(m.persistentGainCap, 1 + m.persistentGainPerDay * Math.max(0, ageHours / 24));
  }
  return sigma * wetFactor(lwc);
}

/**
 * Strength (kPa) of the interface between a layer and the (younger) layer on top of it: the
 * weaker of the two, scaled by how well the upper layer has bonded since it was deposited.
 */
export function interfaceStrength(
  lower: { grain: Grain; density: number },
  upper: { grain: Grain; density: number; ageHours: number },
  snowTemp: number,
  lwc: number,
): number {
  const base = Math.min(
    baseStrength(lower.grain, lower.density),
    baseStrength(upper.grain, upper.density),
  );
  return base * bondingFactor(upper.ageHours, snowTemp) * wetFactor(lwc);
}

/** `bonding.min` → 1 as young snow bonds; faster when warm. */
export function bondingFactor(ageHours: number, snowTemp: number): number {
  const b = STABILITY_MODEL.bonding;
  const tau = b.tau0Hours * Math.exp(-b.tempCoef * Math.min(0, snowTemp));
  return 1 - (1 - b.min) * Math.exp(-Math.max(0, ageHours) / tau);
}

/** Slab shear stress (Pa) from `load` kg/m² (per horizontal m²) on a slope of `slopeDeg`. */
export function slabShear(load: number, slopeDeg: number): number {
  const psi = slopeDeg * DEG;
  return load * G * Math.sin(psi) * Math.cos(psi);
}

/**
 * Additional shear stress (Pa) at slope-normal depth `d` (m) from a skier (line load R) on a
 * slope of `slopeDeg`, after Föhn (1987): max over α of 2R·cosα·sin²α·sin(α+ψ) / (π·d).
 */
export function skierShear(d: number, slopeDeg: number): number {
  return (2 * STABILITY_MODEL.skierLoad * skierFactor(slopeDeg)) / (Math.PI * Math.max(d, 0.05));
}

const SKIER_FACTOR_TABLE = (() => {
  const t = new Float64Array(91);
  for (let s = 0; s <= 90; s++) {
    const psi = s * DEG;
    let best = 0;
    for (let a = 0; a <= 1800; a++) {
      const al = (a / 1800) * Math.PI;
      const v = Math.cos(al) * Math.sin(al) ** 2 * Math.sin(al + psi);
      if (v > best) best = v;
    }
    t[s] = best;
  }
  return t;
})();

function skierFactor(slopeDeg: number): number {
  const s = Math.max(0, Math.min(90, slopeDeg));
  const i = Math.floor(s);
  const f = s - i;
  return SKIER_FACTOR_TABLE[i]! * (1 - f) + SKIER_FACTOR_TABLE[Math.min(90, i + 1)]! * f;
}

/** Per-layer stability for one column; the basis of both the fields and the profile view. */
export interface LayerStability {
  /** Layer index into the column arrays (absolute). */
  layer: number;
  /** Depth of the layer's top below the surface (m, vertical). */
  depthTop: number;
  thickness: number;
  strengthKPa: number;
  /** Shear stress from the slab (kPa). */
  slabShearKPa: number;
  skierShearKPa: number;
  natural: number;
  skier: number;
  /** Slab above this layer is thick enough to count as a slab. */
  isSlab: boolean;
}

export interface ColumnStability {
  layers: LayerStability[];
  /** Index into `layers` of the critical (lowest skier index) layer, or −1. */
  critical: number;
}

/**
 * Stability of every layer in a column. `slopeDeg` is the cell's slope, `airTemp` its air
 * temperature (for the snow-temperature estimate), `now` the evaluation hour.
 */
export function columnStability(
  cols: SnowColumns,
  cell: number,
  slopeDeg: number,
  airTemp: number,
  now: number,
): ColumnStability {
  const m = STABILITY_MODEL;
  const n = cols.count[cell]!;
  const base = cell * cols.maxLayers;
  const layers: LayerStability[] = [];
  if (n === 0) return { layers, critical: -1 };

  let hs = 0;
  for (let k = 0; k < n; k++) hs += cols.mass[base + k]! / cols.density[base + k]!;

  // Ski penetration from the mean density of the top 30 cm.
  let topMass = 0;
  let topDepth = 0;
  for (let k = n - 1; k >= 0 && topDepth < 0.3; k--) {
    const h = cols.mass[base + k]! / cols.density[base + k]!;
    const take = Math.min(h, 0.3 - topDepth);
    topMass += take * cols.density[base + k]!;
    topDepth += take;
  }
  const topDensity = topDepth > 0 ? topMass / topDepth : 300;
  const penetration = Math.min(m.maxPenetration, m.penetrationCoef / topDensity);

  const cosPsi = Math.cos(slopeDeg * DEG);
  let load = 0; // kg/m² above the current layer
  let depth = 0; // vertical depth of the current layer's top
  let critical = -1;
  let best = Infinity;
  for (let k = n - 1; k >= 0; k--) {
    const idx = base + k;
    const rho = cols.density[idx]!;
    const h = cols.mass[idx]! / rho;
    const grain = cols.grain[idx]! as Grain;
    const age = now - cols.depositedAt[idx]!;
    const lwc = volumetricLwc(cols, idx);
    const tSnow = Math.min(0, airTemp) * (1 - Math.min(1, (depth + h / 2) / hs));
    let strength = shearStrength(grain, rho, age, tSnow, lwc);
    if (k < n - 1) {
      // Failure can also happen at the top interface, where younger snow was laid down on it.
      const up = idx + 1;
      const upperAge = now - cols.depositedAt[up]!;
      if (upperAge < age) {
        const bond = interfaceStrength(
          { grain, density: rho },
          { grain: cols.grain[up]! as Grain, density: cols.density[up]!, ageHours: upperAge },
          tSnow,
          Math.max(lwc, volumetricLwc(cols, up)),
        );
        strength = Math.min(strength, bond);
      }
    }
    const isSlab = depth >= m.minSlab;
    const tau = slabShear(load, slopeDeg) / 1000;
    // Slope-normal depth to the failure layer below the ski.
    const dBelowSki = depth * cosPsi - penetration;
    const skierSlab = isSlab && dBelowSki >= m.minSlab;
    const dTau = skierSlab ? skierShear(dBelowSki, slopeDeg) / 1000 : 0;
    const natural = tau > 0 ? Math.min(m.maxIndex, strength / tau) : m.maxIndex;
    const skier =
      skierSlab && tau + dTau > 0 ? Math.min(m.maxIndex, strength / (tau + dTau)) : natural;
    layers.push({
      layer: idx,
      depthTop: depth,
      thickness: h,
      strengthKPa: strength,
      slabShearKPa: tau,
      skierShearKPa: dTau,
      natural,
      skier,
      isSlab,
    });
    // The top layer has no slab over it; loose-snow instability is Phase 4's business.
    if (isSlab && skier < best) {
      best = skier;
      critical = layers.length - 1;
    }
    load += cols.mass[idx]! + cols.water[idx]!;
    depth += h;
  }
  return { layers, critical };
}

export interface StabilityResult {
  /** Skier stability index (SK38) of the critical layer; maxIndex where there's no slab. */
  skier: ScalarField;
  /** Natural stability index of the weakest layer. */
  natural: ScalarField;
  /** Depth (m, vertical) of the critical layer's top below the surface; 0 if none. */
  criticalDepth: ScalarField;
  /** Grain of the critical layer, 255 if none. */
  criticalGrain: Uint8Array;
  /** Thickness (m) and mean density (kg/m³) of the slab above the critical layer. */
  slabThickness: ScalarField;
  slabDensity: ScalarField;
  /** 0…1: how readily a fracture in the critical layer would spread (used by Phase 4). */
  propagation: ScalarField;
  problem: Uint8Array;
}

/** How weak a failure layer of each grain is, for propagation (0…1). */
const PROPAGATION_WEAKNESS: Partial<Record<Grain, number>> = {
  [Grain.SH]: 1,
  [Grain.FC]: 0.85,
  [Grain.DH]: 0.85,
  [Grain.PPgp]: 0.7,
  [Grain.MF]: 0.6,
  [Grain.PP]: 0.5,
  [Grain.DF]: 0.4,
  [Grain.RGwp]: 0.25,
  [Grain.RG]: 0.2,
};

export function computeStability(
  cols: SnowColumns,
  slope: ScalarField,
  airTemp: Float32Array,
  now: number,
): StabilityResult {
  const m = STABILITY_MODEL;
  const spec = cols.spec;
  const n = cols.count.length;
  const skier = createField(spec, m.maxIndex);
  const natural = createField(spec, m.maxIndex);
  const criticalDepth = createField(spec);
  const criticalGrain = new Uint8Array(n).fill(255);
  const slabThickness = createField(spec);
  const slabDensity = createField(spec);
  const propagation = createField(spec);
  const problem = new Uint8Array(n);

  const result: StabilityResult = {
    skier,
    natural,
    criticalDepth,
    criticalGrain,
    slabThickness,
    slabDensity,
    propagation,
    problem,
  };
  for (let i = 0; i < n; i++) updateStabilityCell(result, cols, i, slope, airTemp, now);
  return result;
}

/**
 * (Re)compute the stability fields of one cell in place — used for the whole grid, and for just
 * the cells an avalanche changed.
 */
export function updateStabilityCell(
  result: StabilityResult,
  cols: SnowColumns,
  i: number,
  slope: ScalarField,
  airTemp: Float32Array,
  now: number,
): void {
  const m = STABILITY_MODEL;
  const {
    skier,
    natural,
    criticalDepth,
    criticalGrain,
    slabThickness,
    slabDensity,
    propagation,
    problem,
  } = result;
  skier.data[i] = m.maxIndex;
  natural.data[i] = m.maxIndex;
  criticalDepth.data[i] = 0;
  criticalGrain[i] = 255;
  slabThickness.data[i] = 0;
  slabDensity.data[i] = 0;
  propagation.data[i] = 0;
  problem[i] = AvalancheProblem.None;
  const s = slope.data[i]!;
  const cs = columnStability(cols, i, s, airTemp[i]!, now);
  const wetLoose = s >= m.problemMinSlope && wetSurfaceDepth(cols, i) > 0;
  if (wetLoose) problem[i] = AvalancheProblem.WetSnow;
  let minNatural: number = m.maxIndex;
  for (const l of cs.layers) if (l.isSlab && l.natural < minNatural) minNatural = l.natural;
  natural.data[i] = minNatural;
  if (cs.critical < 0) return;
  const crit = cs.layers[cs.critical]!;
  skier.data[i] = crit.skier;
  criticalDepth.data[i] = crit.depthTop;
  const g = cols.grain[crit.layer]! as Grain;
  criticalGrain[i] = g;

  // Slab = the layers above the critical one (they come first in `layers`).
  let slabMass = 0;
  let slabH = 0;
  let windSlabMass = 0;
  let wetTop = false;
  for (let k = 0; k < cs.critical; k++) {
    const l = cs.layers[k]!;
    const mass = cols.mass[l.layer]!;
    slabMass += mass;
    slabH += l.thickness;
    if (cols.grain[l.layer] === Grain.RGwp) windSlabMass += mass;
    if (l.depthTop < 0.3 && volumetricLwc(cols, l.layer) > EVOLUTION_MODEL.wetThreshold)
      wetTop = true;
  }
  const rhoSlab = slabH > 0 ? slabMass / slabH : 0;
  slabThickness.data[i] = slabH;
  slabDensity.data[i] = rhoSlab;
  const critWet = volumetricLwc(cols, crit.layer) > EVOLUTION_MODEL.wetThreshold;

  // Propagation: weak failure layer × cohesive slab (80 → 200 kg/m³) × slab thick enough.
  const cohesion = smoothstep(80, 200, rhoSlab);
  const thickness = smoothstep(m.minSlab, 0.3, slabH);
  propagation.data[i] = (PROPAGATION_WEAKNESS[g] ?? 0.2) * cohesion * thickness;

  if (s < m.problemMinSlope || crit.skier >= m.fair) return;
  if (wetTop || critWet) problem[i] = AvalancheProblem.WetSnow;
  else if (g === Grain.SH || g === Grain.FC || g === Grain.DH)
    problem[i] = AvalancheProblem.PersistentSlab;
  else if (windSlabMass >= 0.5 * slabMass) problem[i] = AvalancheProblem.WindSlab;
  else if (g === Grain.PP || g === Grain.DF || g === Grain.PPgp)
    problem[i] = AvalancheProblem.NewSnow;
  else problem[i] = AvalancheProblem.WindSlab;
}

/**
 * Thickness (m) of the wet surface layers of a column: the contiguous run from the surface with
 * volumetric LWC ≥ `wetLooseLwc`. 0 if thinner than `wetLooseMinDepth` — nothing to slide.
 */
export function wetSurfaceDepth(cols: SnowColumns, cell: number): number {
  const m = STABILITY_MODEL;
  const base = cell * cols.maxLayers;
  let depth = 0;
  for (let k = cols.count[cell]! - 1; k >= 0; k--) {
    if (volumetricLwc(cols, base + k) < m.wetLooseLwc) break;
    depth += cols.mass[base + k]! / cols.density[base + k]!;
  }
  return depth >= m.wetLooseMinDepth ? depth : 0;
}

/** Hand hardness index from density (rough, for the profile view): F, 4F, 1F, P, K. */
export function handHardness(density: number): 'F' | '4F' | '1F' | 'P' | 'K' {
  if (density < 150) return 'F';
  if (density < 250) return '4F';
  if (density < 350) return '1F';
  if (density < 450) return 'P';
  return 'K';
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
